import { Observable } from 'rxjs'
import Member from 'iam/models/Member'
import { scan, tap, share, switchMap } from 'rxjs/operators'
import { Id } from 'types/definitions'
import memoize from 'lodash/memoize'
import { loadIntercom } from './intercom-loader'

export const JOB_DETAILS_FEATURE_PREVIEW_ATTR =
  'new_job_details_page_product_tour'

/**
 * Manages the Intercom integration.
 */
export function IntercomIntegration(
  currentMember$: Observable<Member>,
  getHash: (workspaceId: string) => Promise<string>,
  isFirmAdmin: () => boolean
) {
  // Only fetch the member hash once per member.
  const getMemberHash = memoize((member: Member) =>
    getHash(Id.assert(member.workspace.id))
  )

  const initialState: State = { type: 'Shutdown' }
  const stateMachine$ = currentMember$.pipe(
    switchMap(async (member): Promise<State> => {
      const settings = member.workspace.features.intercom
      const appId = settings.appId
      if (!settings.enabled || !appId) {
        return { type: 'Shutdown' }
      }

      return {
        member,
        appId,
        type: 'Boot',
        userHash: await getMemberHash(member),
        reboot: false,
      }
    }),
    scan<State, State>((prev, next) => {
      // Ensure that we shut down first in case of consecutive boots.
      if (prev.type === 'Boot' && next.type === 'Boot') {
        return {
          ...next,
          reboot: true,
        }
      }

      return next
    }, initialState),
    share()
  )

  return stateMachine$.pipe(
    tap((state) => {
      if (isFirmAdmin()) {
        return
      }

      const Intercom = (...args: any[]) => (window as any).Intercom?.(...args)
      switch (state.type) {
        case 'Boot': {
          // Memoized.
          loadIntercom()
          if (state.reboot) {
            Intercom('shutdown')
          }
          Intercom('boot', {
            app_id: state.appId,
            name: state.member.displayName,
            email: state.member.email,
            avatar: state.member.avatar,
            phone: state.member.phone,
            user_hash: state.userHash,
            user_id: state.member.user.id,
            // Unix time.
            created_at: (state.member.dateCreated.valueOf() / 1000).toString(),
          })

          break
        }
        case 'Shutdown': {
          Intercom('shutdown')
          break
        }
      }
    })
  )
}

export function setIntercomProductTour(
  featureName: string,
  productTour: string
) {
  const Intercom = (...args: any[]) => (window as any).Intercom?.(...args)
  Intercom('update', { [featureName]: productTour })
}

/**
 * Action types.
 */
type State = Boot | Shutdown

/**
 * Boots Intercom.
 */
interface Boot {
  type: 'Boot'
  appId: string
  userHash: string
  member: Member
  /**
   * If `true`, should shutdown first.
   */
  reboot: boolean
}

/**
 * Shuts down Intercom.
 */
interface Shutdown {
  type: 'Shutdown'
}
